import { ChevronDown } from 'lucide-react';
import { useId } from 'react';
import { createPortal } from 'react-dom';
import { LiksDropdownPanel } from './LiksDropdownPanel';
import type { LiksDropdownProps } from './LiksDropdown.types';
import { useLiksDropdownState } from './useLiksDropdownState';
import './LiksDropdown.css';

export type {
  LiksDropdownOption,
  LiksDropdownProps,
  LiksDropdownVariant,
} from './LiksDropdown.types';

export function LiksDropdown({
  value,
  onChange,
  options,
  'aria-label': ariaLabel,
  variant = 'on-light',
  className,
  id,
  disabled = false,
  placeholder,
  filterable = false,
  filterPlaceholder,
  filterAriaLabel,
  filterEmptyLabel,
  filterClearLabel,
}: LiksDropdownProps) {
  const generatedId = useId();
  const triggerId = id ?? generatedId;
  const listboxId = `${triggerId}-listbox`;
  const filterId = `${triggerId}-filter`;

  const {
    rootRef,
    triggerRef,
    panelRef,
    optionsListRef,
    filterInputRef,
    optionRefs,
    isOpen,
    activeIndex,
    panelStyle,
    filterQuery,
    setFilterQuery,
    visibleOptions,
    selectedOption,
    selectedLabel,
    activeOptionId,
    open,
    close,
    selectIndex,
    setActiveIndex,
    handleTriggerKeyDown,
    handleFilterKeyDown,
  } = useLiksDropdownState({
    value,
    onChange,
    options,
    disabled,
    filterable,
    listboxId,
    placeholder,
  });

  const panel =
    isOpen &&
    options.length > 0 &&
    typeof document !== 'undefined' &&
    createPortal(
      <LiksDropdownPanel
        panelRef={panelRef}
        optionsListRef={optionsListRef}
        filterInputRef={filterInputRef}
        optionRefs={optionRefs}
        panelStyle={panelStyle}
        variant={variant}
        filterable={filterable}
        filterId={filterId}
        listboxId={listboxId}
        ariaLabel={ariaLabel}
        filterQuery={filterQuery}
        filterPlaceholder={filterPlaceholder}
        filterAriaLabel={filterAriaLabel}
        filterClearLabel={filterClearLabel}
        filterEmptyLabel={filterEmptyLabel}
        activeOptionId={activeOptionId}
        activeIndex={activeIndex}
        value={value}
        visibleOptions={visibleOptions}
        onFilterChange={(nextQuery) => {
          setFilterQuery(nextQuery);
          // Keep highlight on the first match while typing.
          if (nextQuery !== '') {
            setActiveIndex(0);
          }
        }}
        onFilterKeyDown={handleFilterKeyDown}
        onClearFilter={() => {
          setFilterQuery('');
          filterInputRef.current?.focus();
        }}
        onActiveIndexChange={setActiveIndex}
        onSelectIndex={selectIndex}
      />,
      document.body,
    );

  return (
    <div
      ref={rootRef}
      className={[
        'liks-dropdown',
        `liks-dropdown--${variant}`,
        isOpen && 'liks-dropdown--open',
        disabled && 'liks-dropdown--disabled',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        className="liks-dropdown__trigger"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-activedescendant={
          isOpen && !filterable ? activeOptionId : undefined
        }
        onClick={() => {
          if (isOpen) {
            close();
            return;
          }
          open();
        }}
        onKeyDown={handleTriggerKeyDown}
      >
        <span
          className={[
            'liks-dropdown__trigger-label',
            !selectedOption && placeholder
              ? 'liks-dropdown__trigger-label--placeholder'
              : null,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {selectedLabel}
        </span>
        <span className="liks-dropdown__chevron-group" aria-hidden>
          <ChevronDown
            className={[
              'liks-dropdown__chevron',
              isOpen && 'liks-dropdown__chevron--open',
            ]
              .filter(Boolean)
              .join(' ')}
            strokeWidth={2}
          />
        </span>
      </button>
      {panel}
    </div>
  );
}
